// Canonical enums for two distinct concept families:
//
//  • CLAIM_TYPES — workspace-level claim posture (what stage / track is this
//    claim on right now). Used by the workspace switcher, 526EZ field, and
//    PDF cover pages.
//
//  • THEORIES — per-condition theory of service connection. Drives which
//    nexus checklist runs in Visit Prep, which prompts show in the Statement
//    Builder, and which PDF section template renders.
//
// Both objects are frozen and validated against their option-arrays /
// short-label maps at module load. A drift between any of those structures
// throws immediately so a typo can't silently render an empty dropdown or
// fall through to the "Direct" default in the PDF.
//
// Callsites should import the constants (`THEORIES.direct`,
// `CLAIM_TYPES.bdd`) instead of typing the canonical strings — that way a
// rename only needs to happen here, and ESLint flags `THEORIES.tipo` as
// undefined property at lint time.

export const CLAIM_TYPES = Object.freeze({
  bdd: "BDD / Pre-discharge",
  original: "Original disability claim",
  increase: "Claim for increase",
  supplemental: "Supplemental Claim",
  higherLevelReview: "Higher-Level Review",
  boardAppeal: "Board Appeal",
});
export const CLAIM_TYPE_OPTIONS = Object.values(CLAIM_TYPES);

export const THEORIES = Object.freeze({
  direct: "Direct service connection",
  secondary: "Secondary to service-connected condition",
  aggravation: "Aggravation of pre-existing condition",
  presumptive: "Presumptive service connection",
  pact: "PACT Act / toxic exposure",
  increase: "Increase of existing rating",
  supplemental: "Supplemental / previously denied",
});
export const THEORY_OPTIONS = Object.values(THEORIES);

/** Short label used in tight UI surfaces (claim-tracker chips, PDFs). */
export const THEORY_SHORT_LABEL = Object.freeze({
  [THEORIES.direct]: "Direct",
  [THEORIES.secondary]: "Secondary",
  [THEORIES.aggravation]: "Aggravation (pre-existing)",
  [THEORIES.presumptive]: "Presumptive",
  [THEORIES.pact]: "PACT Act / toxic exposure",
  [THEORIES.increase]: "Increase",
  [THEORIES.supplemental]: "Supplemental",
});

// --- Module-load parity guards. ---------------------------------------------
const _claimTypeSet = new Set(CLAIM_TYPE_OPTIONS);
const _theorySet = new Set(THEORY_OPTIONS);
for (const v of _theorySet) {
  if (!(v in THEORY_SHORT_LABEL)) {
    throw new Error(`claimEnums: THEORY_SHORT_LABEL missing entry for "${v}".`);
  }
}
for (const k of Object.keys(THEORY_SHORT_LABEL)) {
  if (!_theorySet.has(k)) {
    throw new Error(`claimEnums: THEORY_SHORT_LABEL has stale key "${k}" not in THEORIES.`);
  }
}

/** Truthy if value matches a known theory canonical string. */
export function isValidTheory(t) { return _theorySet.has(t); }
/** Truthy if value matches a known workspace claim-type canonical string. */
export function isValidClaimType(t) { return _claimTypeSet.has(t); }
/** Map any theory string (canonical or unknown) to its short UI label. */
export function shortTheoryLabel(t) { return THEORY_SHORT_LABEL[t] || t || "—"; }
